import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Mail, Phone, MapPin } from 'lucide-react';
import { useServices, useRegions, localized, regionPath } from '../hooks/useServices';
import { servicePath } from './ServiceCard';
import { FooterGoogleLinks } from './GoogleBusiness';

const LOGO = `${process.env.REACT_APP_BACKEND_URL}/api/uploads/logo_transparent.png`;
const linkCls = 'text-slate-400 hover:text-[#3b8dff] text-sm transition-colors';
const headCls = 'font-semibold text-white mb-4 text-sm uppercase tracking-wider';

export default function Footer() {
  const { t, language, lp } = useLanguage();
  const services = useServices();
  const regions = useRegions();
  const year = new Date().getFullYear();

  return (
    <footer className="bg-[#0F172A] text-white" data-testid="main-footer">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-20">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-12">
          <div>
            <img src={LOGO} alt="ITCS Industrial" width="98" height="48" loading="lazy" className="h-12 w-auto mb-5 brightness-0 invert" />
            <p className="text-slate-400 text-sm leading-relaxed">{t('footer.description')}</p>
          </div>

          <div>
            <h2 className={headCls}>{t('footer.services')}</h2>
            <ul className="space-y-3">
              {services.map(s => (
                <li key={s.key}>
                  <Link to={lp(servicePath(s))} className={linkCls} data-testid={`footer-service-${s.key}`}>{localized(s, 'title', language)}</Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h2 className={headCls}>{t('footer.regions')}</h2>
            <ul className="space-y-3" data-testid="footer-regions">
              {regions.map(r => (
                <li key={r.key}>
                  <Link to={regionPath(r)} className={linkCls} data-testid={`footer-region-${r.key}`}>{r.name}</Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h2 className={headCls}>{t('footer.company')}</h2>
            <ul className="space-y-3">
              <li><Link to={lp('/about')} className={linkCls}>{t('nav.about')}</Link></li>
              <li><Link to={lp('/projects')} className={linkCls}>{t('nav.projects')}</Link></li>
              <li><Link to={lp('/planner')} className={linkCls}>{t('nav.planner')}</Link></li>
              <li><Link to={lp('/blog')} className={linkCls}>{t('nav.blog')}</Link></li>
              <li><Link to={lp('/contact')} className={linkCls}>{t('nav.contact')}</Link></li>
            </ul>
          </div>

          <div>
            <h2 className={headCls}>{t('nav.contact')}</h2>
            <address className="not-italic space-y-3">
              <p className="flex items-start gap-3 text-slate-400 text-sm">
                <MapPin className="w-4 h-4 text-[#3b8dff] shrink-0 mt-0.5" />
                <span>Zur Neuen Brücke 4<br />01109 Dresden</span>
              </p>
              <p className="flex items-center gap-3 text-slate-400 text-sm">
                <Mail className="w-4 h-4 text-[#3b8dff] shrink-0" />
                <a href="mailto:info@itcs-global.com" className="hover:text-[#3b8dff] transition-colors">{t('contact.emailVal')}</a>
              </p>
              <p className="flex items-center gap-3 text-slate-400 text-sm">
                <Phone className="w-4 h-4 text-[#3b8dff] shrink-0" />
                <a href="tel:+491728116925" className="hover:text-[#3b8dff] transition-colors">{t('contact.phoneVal')}</a>
              </p>
            </address>
            <div className="mt-4"><FooterGoogleLinks /></div>
          </div>
        </div>

        <div className="border-t border-slate-800 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-slate-500 text-xs">
            &copy; {year} ITCS Industrial Trade & Construction Systems GmbH. {t('footer.rights')}
          </p>
          <div className="flex gap-6">
            <Link to={lp('/impressum')} className="text-slate-500 hover:text-slate-300 text-xs transition-colors">{t('footer.impressum')}</Link>
            <Link to={lp('/datenschutz')} className="text-slate-500 hover:text-slate-300 text-xs transition-colors">{t('footer.datenschutz')}</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
